const {
  BESTEK_DECISION,
  BESTEK_LINE_REQUIREMENT,
  BESTEK_LINE_REQUIREMENT_DESCRIPTIONS,
  BESTEK_OUTCOME,
  BESTEK_RAMING_ACTION,
  getBestekExpiresAt,
  hasOpenBestekIncompleteness,
  isBestekExpired,
} = require("../types/bestek.types");
const { formatCurrency } = require("./format");
const {
  collectAllNewProductOptions,
  getRamingTotal,
  getRepairRamingen,
  getEstimateLineId,
  isReplacementHostRaming,
} = require("./bestekRamingen");

const toNumber = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (value == null || value === "") return 0;
  const normalized = String(value)
    .trim()
    .replace(/\s/g, "")
    .replace(/\.(?=.*\.)/g, "")
    .replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const getLineRequirement = (line = {}) =>
  line.lineRequirement || BESTEK_LINE_REQUIREMENT.MOET;

const isMustLine = (line = {}) =>
  getLineRequirement(line) === BESTEK_LINE_REQUIREMENT.MOET;

const isOptionalLine = (line = {}) => {
  const req = getLineRequirement(line);
  return (
    req === BESTEK_LINE_REQUIREMENT.MAG || req === BESTEK_LINE_REQUIREMENT.KAN
  );
};

const getRepairLineTotal = (line = {}) =>
  toNumber(line.total ?? line.RAMUTOT ?? line.RAMRPMTOT);

const mapCustomerRamingView = (raming = {}) => {
  const ramingId = getEstimateLineId(raming);
  const materialLines = (raming.materialLines || []).map((line) => ({
    lineId: getEstimateLineId(line),
    description: line.description || "",
    artNr: line.artNr || "",
    quantity: line.quantity ?? null,
    unit: line.unit || "",
    amount: getRepairLineTotal(line),
    artikelStatus: line.artikelStatus || "",
    isReplacementPart: line.isReplacementPart === true,
  }));

  return {
    ramingId,
    lineId: ramingId,
    lineNr: raming.lineNr,
    requirement: getLineRequirement(raming),
    requirementDescription:
      BESTEK_LINE_REQUIREMENT_DESCRIPTIONS[getLineRequirement(raming)] || "",
    description: raming.description || "",
    laborAmount: getRepairLineTotal(raming),
    laborMinutes: Math.round(toNumber(raming.timeSeconds) / 60),
    materialLines,
    amount: getRamingTotal(raming),
    isReplacementHost: isReplacementHostRaming(raming),
    newProductOptions: (raming.newProductOptions || []).map((p) => ({
      _id: p._id,
      description: p.description,
      artNr: p.artNr,
      unitPrice: p.unitPrice,
      grossUnitPrice: p.grossUnitPrice,
    })),
  };
};

const buildMustEstimateLines = (bestek = {}) =>
  getRepairRamingen(bestek)
    .filter((raming) => isMustLine(raming) && !isReplacementHostRaming(raming))
    .map(mapCustomerRamingView)
    .filter((line) => line.ramingId);

const buildOptionalEstimateLines = (bestek = {}) =>
  getRepairRamingen(bestek)
    .filter(
      (raming) => isOptionalLine(raming) && !isReplacementHostRaming(raming)
    )
    .map(mapCustomerRamingView)
    .filter((line) => line.ramingId);

const sumRepairEstimateForSelection = (
  bestek = {},
  selectedOptionalLineIds = []
) => {
  const selectedSet = new Set(
    (selectedOptionalLineIds || []).map((id) => String(id))
  );
  return getRepairRamingen(bestek).reduce((sum, raming) => {
    const ramingId = getEstimateLineId(raming);
    if (isMustLine(raming)) {
      return sum + getRamingTotal(raming);
    }
    if (selectedSet.has(ramingId)) {
      return sum + getRamingTotal(raming);
    }
    return sum;
  }, 0);
};

/**
 * Sum cost components on a diagnosis line (technician task and/or RPM material).
 * HTTOTWERK / HTTOTKM / HTTOTVERPL are euro totals from FinERP (not time/km qty).
 * Diagnostic materials (herstrepmat / rpm) are stored on HTTOTEXTRA.
 */
const sumDiagnosisLineCost = (line = {}) => {
  const sum =
    toNumber(line.HTTOTWERK) +
    toNumber(line.HTTOTKM) +
    toNumber(line.HTTOTVERPL) +
    toNumber(line.HTTOTEXTRA) +
    toNumber(line.HTBESTKK) +
    toNumber(line.HTTRANSK) +
    toNumber(line.HTEXTRAK) +
    toNumber(line.HTLEVK) +
    toNumber(line.HTKLK);
  return Math.round(sum * 100) / 100;
};

const sumDiagnosisActualTotal = (diagnosisLines = []) =>
  diagnosisLines.reduce((sum, line) => sum + sumDiagnosisLineCost(line), 0);

const sumRepairLaborTotal = (bestek = {}) =>
  getRepairRamingen(bestek).reduce(
    (sum, raming) => sum + getRepairLineTotal(raming),
    0
  );

const sumRepairMaterialsTotal = (bestek = {}) =>
  getRepairRamingen(bestek).reduce(
    (sum, raming) =>
      sum +
      (raming.materialLines || []).reduce(
        (matSum, line) => matSum + getRepairLineTotal(line),
        0
      ),
    0
  );

const computeBestekTotals = (bestek = {}) => {
  const diagnosisActualTotal = sumDiagnosisActualTotal(
    bestek.diagnosisLines || []
  );
  const repairLaborTotal = sumRepairLaborTotal(bestek);
  const repairMaterialsTotal = sumRepairMaterialsTotal(bestek);
  /** Repair lines only (no diagnosis) — for service raming breakdowns. */
  const repairEstimateTotal = repairLaborTotal + repairMaterialsTotal;
  const repairEstimateMinimum = sumRepairEstimateForSelection(bestek, []);
  const forfaitDiagnosticCost = toNumber(bestek.forfaitDiagnosticCost);
  /** Herstellen route totals: diagnose is always included when repairing. */
  const repairRouteMinimum =
    Math.round((diagnosisActualTotal + repairEstimateMinimum) * 100) / 100;
  const repairRouteMaximum =
    Math.round((diagnosisActualTotal + repairEstimateTotal) * 100) / 100;

  return {
    diagnosisActualTotal,
    repairLaborTotal,
    repairMaterialsTotal,
    repairEstimateTotal,
    repairEstimateMinimum,
    repairRouteMinimum,
    repairRouteMaximum,
    forfaitDiagnosticCost,
  };
};

const findProductOnRaming = (raming, productId) => {
  const products = raming.newProductOptions || [];
  if (!products.length) return null;
  if (productId == null || productId === "") return products[0];
  return (
    products.find((p) => String(p._id || p.id) === String(productId)) || null
  );
};

/**
 * Normalize per-raming choices.
 * Must lines cannot skip unless allowMustSkip (document-level "Niet herstellen").
 */
const normalizeRamingChoices = (
  bestek = {},
  ramingChoices = [],
  { allowMustSkip = false } = {}
) => {
  const byId = new Map(
    (ramingChoices || []).map((choice) => [String(choice.ramingId), choice])
  );
  const ramingen = getRepairRamingen(bestek);

  return ramingen
    .map((raming) => {
      const ramingId = getEstimateLineId(raming);
      if (!ramingId) return null;
      const incoming = byId.get(ramingId) || {};
      // Unchosen optional lines must not default to REPAIR (inflates live totals).
      let action =
        incoming.action ||
        (isMustLine(raming)
          ? BESTEK_RAMING_ACTION.REPAIR
          : BESTEK_RAMING_ACTION.SKIP);
      const products = raming.newProductOptions || [];

      if (!Object.values(BESTEK_RAMING_ACTION).includes(action)) {
        action = isMustLine(raming)
          ? BESTEK_RAMING_ACTION.REPAIR
          : BESTEK_RAMING_ACTION.SKIP;
      }
      if (
        action === BESTEK_RAMING_ACTION.SKIP &&
        isMustLine(raming) &&
        !allowMustSkip
      ) {
        action =
          products.length > 0
            ? BESTEK_RAMING_ACTION.BUY_NEW
            : BESTEK_RAMING_ACTION.REPAIR;
      }
      if (action === BESTEK_RAMING_ACTION.BUY_NEW && products.length === 0) {
        action = isMustLine(raming)
          ? BESTEK_RAMING_ACTION.REPAIR
          : BESTEK_RAMING_ACTION.SKIP;
      }

      let selectedNewMachineId = null;
      if (action === BESTEK_RAMING_ACTION.BUY_NEW) {
        const product = findProductOnRaming(
          raming,
          incoming.selectedNewMachineId
        );
        selectedNewMachineId = product
          ? String(product._id || product.id)
          : null;
        if (!selectedNewMachineId) {
          action = isMustLine(raming)
            ? BESTEK_RAMING_ACTION.REPAIR
            : BESTEK_RAMING_ACTION.SKIP;
        }
      }

      return { ramingId, action, selectedNewMachineId };
    })
    .filter(Boolean);
};

/** Build default per-raming choices (repair all must + none of the optionals). */
const buildDefaultRamingChoices = (bestek = {}) =>
  getRepairRamingen(bestek)
    .map((raming) => {
      const ramingId = getEstimateLineId(raming);
      if (!ramingId) return null;
      return {
        ramingId,
        action: isMustLine(raming)
          ? BESTEK_RAMING_ACTION.REPAIR
          : BESTEK_RAMING_ACTION.SKIP,
        selectedNewMachineId: null,
      };
    })
    .filter(Boolean);

/**
 * Legacy global choice → per-raming choices.
 */
const ramingChoicesFromLegacyDecision = (
  bestek = {},
  decision,
  {
    selectedOptionalLineIds = [],
    selectedRamingId = null,
    selectedNewMachineId = null,
  } = {}
) => {
  const optionalSet = new Set(
    (selectedOptionalLineIds || []).map((id) => String(id))
  );

  return getRepairRamingen(bestek)
    .map((raming) => {
      const ramingId = getEstimateLineId(raming);
      if (!ramingId) return null;

      if (decision === BESTEK_DECISION.NO_REPAIR) {
        return {
          ramingId,
          action: BESTEK_RAMING_ACTION.SKIP,
          selectedNewMachineId: null,
        };
      }

      if (decision === BESTEK_DECISION.BUY_NEW) {
        const isSelectedRaming =
          !selectedRamingId || String(selectedRamingId) === String(ramingId);
        if (isSelectedRaming && (raming.newProductOptions || []).length > 0) {
          const product = findProductOnRaming(raming, selectedNewMachineId);
          return {
            ramingId,
            action: BESTEK_RAMING_ACTION.BUY_NEW,
            selectedNewMachineId: product
              ? String(product._id || product.id)
              : null,
          };
        }
        return {
          ramingId,
          action: BESTEK_RAMING_ACTION.SKIP,
          selectedNewMachineId: null,
        };
      }

      // REPAIR
      if (isMustLine(raming) || optionalSet.has(String(ramingId))) {
        return {
          ramingId,
          action: BESTEK_RAMING_ACTION.REPAIR,
          selectedNewMachineId: null,
        };
      }
      return {
        ramingId,
        action: BESTEK_RAMING_ACTION.SKIP,
        selectedNewMachineId: null,
      };
    })
    .filter(Boolean);
};

const deriveOverallDecision = (ramingChoices = []) => {
  const actions = (ramingChoices || []).map((c) => c.action);
  if (actions.some((a) => a === BESTEK_RAMING_ACTION.REPAIR)) {
    return BESTEK_DECISION.REPAIR;
  }
  if (actions.some((a) => a === BESTEK_RAMING_ACTION.BUY_NEW)) {
    return BESTEK_DECISION.BUY_NEW;
  }
  return BESTEK_DECISION.NO_REPAIR;
};

const computeChoiceCostFromRamingChoices = (
  bestek = {},
  ramingChoices = [],
  { allowMustSkip = false } = {}
) => {
  const totals = computeBestekTotals(bestek);
  const normalized = normalizeRamingChoices(bestek, ramingChoices, {
    allowMustSkip,
  });
  const ramingenById = new Map(
    getRepairRamingen(bestek).map((r) => [getEstimateLineId(r), r])
  );

  let repairSum = 0;
  let productSum = 0;
  let hasRepair = false;
  let hasBuyNew = false;

  for (const choice of normalized) {
    const raming = ramingenById.get(choice.ramingId);
    if (!raming) continue;
    if (choice.action === BESTEK_RAMING_ACTION.REPAIR) {
      hasRepair = true;
      repairSum += getRamingTotal(raming);
    } else if (choice.action === BESTEK_RAMING_ACTION.BUY_NEW) {
      hasBuyNew = true;
      const product = findProductOnRaming(raming, choice.selectedNewMachineId);
      productSum += toNumber(product?.unitPrice);
    }
  }

  if (hasRepair) {
    return Math.round((totals.diagnosisActualTotal + repairSum + productSum) * 100) / 100;
  }
  if (hasBuyNew) {
    // Diagnosis waived when only replacing with new product(s)
    return Math.round(productSum * 100) / 100;
  }
  return totals.forfaitDiagnosticCost;
};

const computeChoiceCost = (
  bestek = {},
  decision,
  {
    newMachineUnitPrice,
    selectedOptionalLineIds = [],
    selectedRamingId = null,
    selectedNewMachineId = null,
    ramingChoices = null,
  } = {}
) => {
  if (Array.isArray(ramingChoices) && ramingChoices.length > 0) {
    return computeChoiceCostFromRamingChoices(bestek, ramingChoices);
  }

  const totals = computeBestekTotals(bestek);

  switch (decision) {
    case BESTEK_DECISION.REPAIR: {
      const repairPart = sumRepairEstimateForSelection(
        bestek,
        selectedOptionalLineIds
      );
      return Math.round((totals.diagnosisActualTotal + repairPart) * 100) / 100;
    }
    case BESTEK_DECISION.BUY_NEW: {
      if (newMachineUnitPrice != null) {
        return toNumber(newMachineUnitPrice);
      }
      const products = collectAllNewProductOptions(bestek);
      const selected = products.find(
        (p) =>
          (!selectedRamingId ||
            String(p.ramingId) === String(selectedRamingId)) &&
          (!selectedNewMachineId ||
            String(p._id || p.id) === String(selectedNewMachineId))
      );
      return toNumber(selected?.unitPrice ?? products[0]?.unitPrice);
    }
    case BESTEK_DECISION.NO_REPAIR:
      return totals.forfaitDiagnosticCost;
    default:
      return null;
  }
};

const getValidOptionalLineIds = (bestek = {}) =>
  new Set(
    buildOptionalEstimateLines(bestek).map((line) => String(line.ramingId))
  );

const normalizeSelectedOptionalLineIds = (bestek = {}, selectedOptionalLineIds) => {
  if (!Array.isArray(selectedOptionalLineIds)) return [];
  const valid = getValidOptionalLineIds(bestek);
  return [...new Set(selectedOptionalLineIds.map(String))].filter((id) =>
    valid.has(id)
  );
};

/** Legacy flat choice options (kept for PDF / older UIs). */
const buildChoiceOptions = (bestek = {}, { selectedOptionalLineIds = [] } = {}) => {
  const outcome = bestek.outcome || BESTEK_OUTCOME.REPAIRABLE;
  const totals = computeBestekTotals(bestek);
  const optionalEstimateLines = buildOptionalEstimateLines(bestek);
  const options = [];

  if (outcome === BESTEK_OUTCOME.REPAIRABLE) {
    const repairAmount = computeChoiceCost(bestek, BESTEK_DECISION.REPAIR, {
      selectedOptionalLineIds,
    });
    options.push({
      decision: BESTEK_DECISION.REPAIR,
      label: { nl: "Herstellen" },
      amount: repairAmount,
      includesDiagnosis: true,
      includesRepair: true,
      hasOptionalLines: optionalEstimateLines.length > 0,
      repairEstimateMinimum: totals.repairRouteMinimum,
      repairEstimateMaximum: totals.repairRouteMaximum,
    });
  }

  const products = collectAllNewProductOptions(bestek);
  for (const product of products) {
    options.push({
      decision: BESTEK_DECISION.BUY_NEW,
      label: {
        nl: product.description
          ? `Nieuw product: ${product.description}${
              product.ramingDescription
                ? ` (i.p.v. ${product.ramingDescription})`
                : ""
            } (richtprijs onder voorbehoud)`
          : "Nieuw product kopen (richtprijs onder voorbehoud)",
      },
      amount: computeChoiceCost(bestek, BESTEK_DECISION.BUY_NEW, {
        newMachineUnitPrice: product.unitPrice,
      }),
      includesDiagnosis: false,
      includesRepair: false,
      newMachineId: product._id?.toString?.() || product.id || null,
      ramingId: product.ramingId,
      newMachine: product,
      allowKeepOld: bestek.allowBuyNewAndKeepOld === true,
    });
  }

  options.push({
    decision: BESTEK_DECISION.NO_REPAIR,
    label: { nl: "Niet herstellen" },
    amount: computeChoiceCost(bestek, BESTEK_DECISION.NO_REPAIR),
    includesDiagnosis: false,
    includesRepair: false,
    usesForfait: true,
  });

  return { totals, options, optionalEstimateLines };
};

const formatCustomerChoiceOptionLabel = (option = {}) => {
  const price = formatCurrency(option.amount ?? 0);

  switch (option.decision) {
    case BESTEK_DECISION.REPAIR:
      return `Herstellen: Diagnose + herstelling: ${price}`;
    case BESTEK_DECISION.BUY_NEW: {
      const description =
        option.newMachine?.description || option.label?.productDescription || "";
      const productSuffix = description ? ` (${description})` : "";
      return `Nieuw product${productSuffix}: Diagnose- en behandelingskost vervallen; richtprijs onder voorbehoud (prijs kan nog wijzigen): ${price}`;
    }
    case BESTEK_DECISION.NO_REPAIR:
      return `Niet herstellen: Behandelingskost: ${price}`;
    default:
      return option.label?.nl || price;
  }
};

const buildCustomerBestekView = (bestek) => {
  if (!bestek) return null;
  const { totals, options, optionalEstimateLines } = buildChoiceOptions(bestek);
  const mustEstimateLines = buildMustEstimateLines(bestek);
  const repairRamingen = getRepairRamingen(bestek).map(mapCustomerRamingView);

  return {
    _id: bestek._id,
    DOSSIERNR: bestek.DOSSIERNR,
    DOKNR: bestek.DOKNR,
    FICHE: bestek.FICHE,
    outcome: bestek.outcome,
    bestekType: bestek.bestekType || "standard",
    status: bestek.status || null,
    isSubmitted: bestek.isSubmitted === true,
    allowBuyNewAndKeepOld: bestek.allowBuyNewAndKeepOld === true,
    requiresImmediatePayment: bestek.requiresImmediatePayment === true,
    customerReferenceRequired: bestek.customerReferenceRequired === true,
    /** Open incompleteness report: page stays readable, no decision possible. */
    onHold: hasOpenBestekIncompleteness(bestek),
    forfaitDiagnosticCost: totals.forfaitDiagnosticCost,
    diagnosisActualTotal: totals.diagnosisActualTotal,
    repairEstimateTotal: totals.repairEstimateTotal,
    repairEstimateMinimum: totals.repairEstimateMinimum,
    /** Herstellen option range including diagnose (what the customer pays if repairing). */
    repairRouteMinimum: totals.repairRouteMinimum,
    repairRouteMaximum: totals.repairRouteMaximum,
    repairLaborTotal: totals.repairLaborTotal,
    repairMaterialsTotal: totals.repairMaterialsTotal,
    repairRamingen,
    mustEstimateLines,
    optionalEstimateLines,
    newMachineOptions: collectAllNewProductOptions(bestek).map((m) => ({
      _id: m._id,
      description: m.description,
      artNr: m.artNr,
      unitPrice: m.unitPrice,
      grossUnitPrice: m.grossUnitPrice,
      ramingId: m.ramingId,
      ramingDescription: m.ramingDescription,
    })),
    choiceOptions: options,
    defaultRamingChoices: buildDefaultRamingChoices(bestek),
    customerChoice: bestek.customerChoice || null,
    customerName: bestek.customerName,
    contactName: bestek.contactName || "",
    contactEmail: bestek.contactEmail || "",
    machineName: bestek.machineName,
    reason: bestek.reason,
    remarks: bestek.remarks,
    submittedAt: bestek.submittedAt || null,
    expiresAt: getBestekExpiresAt(bestek),
    isExpired: isBestekExpired(bestek),
    publishedVersion: Number(bestek.currentPublishedVersion) || null,
    publishedAt:
      (Array.isArray(bestek.publishedVersions)
        ? bestek.publishedVersions.find(
            (v) =>
              Number(v.version) === Number(bestek.currentPublishedVersion)
          )?.publishedAt
        : null) ||
      bestek.submittedAt ||
      null,
    // Paths stay on the server; photos are fetched by index.
    photos: (Array.isArray(bestek.photos) ? bestek.photos : []).map((photo, index) => ({
      index,
      name: photo?.name || `foto-${index + 1}.jpg`,
    })),
    incompletenessReports: (bestek.incompletenessReports || []).map((report) => ({
      comment: report.comment,
      reportedAt: report.reportedAt,
      publishedVersion: report.publishedVersion || null,
    })),
  };
};

// Legacy helpers — flatten ramingen for old callers
const collectEstimateLines = (bestek = {}) =>
  getRepairRamingen(bestek).flatMap((raming) => {
    const ramingId = getEstimateLineId(raming);
    const labor = {
      ...raming,
      lineType: "raming",
      lineId: ramingId,
    };
    const materials = (raming.materialLines || []).map((line) => ({
      ...line,
      lineType: "material",
      lineId: getEstimateLineId(line),
      parentRamingId: ramingId,
    }));
    return [labor, ...materials];
  });

module.exports = {
  toNumber,
  getLineRequirement,
  isMustLine,
  isOptionalLine,
  getRepairLineTotal,
  getEstimateLineId,
  collectEstimateLines,
  sumRepairEstimateForSelection,
  buildOptionalEstimateLines,
  buildMustEstimateLines,
  mapCustomerRamingView,
  getValidOptionalLineIds,
  normalizeSelectedOptionalLineIds,
  normalizeRamingChoices,
  buildDefaultRamingChoices,
  ramingChoicesFromLegacyDecision,
  deriveOverallDecision,
  computeChoiceCostFromRamingChoices,
  sumDiagnosisLineCost,
  sumDiagnosisActualTotal,
  sumRepairLaborTotal,
  sumRepairMaterialsTotal,
  computeBestekTotals,
  computeChoiceCost,
  buildChoiceOptions,
  buildCustomerBestekView,
  formatCustomerChoiceOptionLabel,
};
